import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { Dropdown } from "react-bootstrap";

import * as Auth from "@/lib/auth";
import { getBackend } from "@/lib/backend/backend2";
import * as BusinessLogic from "@/lib/businessLogic";
import { classNames, weekNumber, getDateOfISOWeek, TIME_RANGES } from "@/lib/util";
import local_settings, { FIELD_SHOW_PLANNING_DETAILS_FILTER } from "@/lib/localSettings";
import Authorization from "@/modules/shared/Authorization";

import DateTimePicker from "@/modules/shared/ui/datetime/DateTimePicker";
import { NavigationBarWithDropdown, type HandleViewChangeEvent } from "@/modules/pg/shared/NavigationBar";
import DashboardStandalone from "@/modules/shared/dashboard/DashboardStandalone";

import MicroplanSummary, { initMicroplanSummaryFilterProps } from "./weeklyRealization/core/MicroplanSummary";
import { MICROPLAN_EXPORT_TYPE } from "@/modules/pg/shared/types";

import OrdersAndShifts from "./ordersAndShifts/OrdersAndShifts";
import PlanningTableApp from "./planningTable/core/PlanningTableApp";
import BatchOperations from "@/modules/pg/shared/batchOperations/BatchOperations";
import BatchOperationsArchive from "./batchOperations/BatchOperationsArchive";
import BatchOperationsStatus from "./batchOperations/BatchOperationsStatus";

import InsightsView from "@/modules/pg/shared/InsightsView";
import Subcontracting from "./subcontracting/Subcontracting";
import PredictionVsReal from "./evaluation/PredictionVsReal";
import ManualEntryOrders from "./manualEntry/ManualEntryOrders";

import ExportToXLSXButton from "./weeklyRealization/export/ExportToXLSXButton";
import { GanttChartSource, GanttChartSourceTypes } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import {
    RESET_FILTERS,
    PlanningTableFiltersActionTypes,
} from "@/modules/pg/shared/planningTable/reducers/filters/filters";

import {
    LINE_GROUP_TAGS,
    BATCHOP_GROUP_TAGS,
    INSIGHT_TYPES,
    INSIGHT_TAGS,
    LINE_GROUP_TAGS_ACCESS,
    BATCHOP_GROUP_TAGS_ACCESS,
} from "@/lib/ManufacturingTags.generated";

import { LINES_VIEWS_VIEW_TYPES, redirectSelectedView } from "@/modules/pg/shared/viewsCommon";
import { MaterialTranslations } from "@/modules/shared/materialTranslations";
import { store } from "@/index";
import WeekPicker from "@/modules/shared/ui/WeekPicker";
import { Toolbar, ToolbarGroup, ToolbarItem } from "@/modules/shared/Toolbar";
import Preplanning from "./preplanning/core/Preplanning";

import { MicroplanSummaryFilterProps } from "@/modules/pg/shared/weeklyRealization/weeklyRealizationCommon";
import { ViewsDropdownOption } from "@/modules/pg/shared/viewsCommon";
import { ScrapUptimeMatrixLines } from "@/modules/pg/shared/weeklyRealization/weeklyRealizationCommon";
import { DropdownItemProps } from "react-bootstrap/esm/DropdownItem";

const VIEW_TYPES = LINES_VIEWS_VIEW_TYPES;

type DashboardViewFilterProps = {
    title?: string;
    title_code?: string;
    dashboard: string;
    line_uuid?: string;
};

type DashboardFilterProps = {
    views: DashboardViewFilterProps[];
    selected: number;
};

type BatchOperationsHistoryFilterProps = {
    order_id: string;
    start_date?: Date;
    end_date?: Date;
    material_ident?: string;
};

type OwnProps = {
    history: History;
    match: match<{ uuid?: string; selected_view?: string; view_params?: string; group_uuid?: string }>;
    location: Location;
    plant_uuid: string;
    line_group_uuid: string;
};

type Props = OwnProps & ReduxProps;

type State = {
    views: ViewsDropdownOption[];
    selected_view: string;
    // Filters for individual views
    microplan_filter: DashboardFilterProps;
    microplan_summary_filter: MicroplanSummaryFilterProps;
    microplan_produced_filter: MicroplanSummaryFilterProps;
    silo_input: boolean;
    batch_archive: boolean;
    batch_status: boolean;
    batch_history_filter: BatchOperationsHistoryFilterProps;
    show_planning_details_filter: boolean;
    scrap_uptime_matrix: ScrapUptimeMatrixLines;
    material_ident_query: string;
};

class LinesView extends React.Component<Props, State> {
    exportToXLSXButtonRef = React.createRef<ExportToXLSXButton>();
    ordersAndShiftsRef = React.createRef<OrdersAndShifts>();

    constructor(props: Props) {
        super(props);
        const show_planning_details_filter = local_settings.getItem(FIELD_SHOW_PLANNING_DETAILS_FILTER);
        this.state = {
            views: [],
            selected_view: props.match.params.selected_view || VIEW_TYPES.gantt.id,
            microplan_filter: { views: [], selected: 0 },
            microplan_summary_filter: initMicroplanSummaryFilterProps(),
            microplan_produced_filter: initMicroplanSummaryFilterProps(),
            silo_input: false,
            batch_archive: false,
            batch_status: false,
            batch_history_filter: { order_id: "" },
            show_planning_details_filter: show_planning_details_filter === "true",
            material_ident_query: "",
            scrap_uptime_matrix: new Map(),
        };
    }

    async componentDidMount() {
        await BusinessLogic.waitForManufacturingAsync();
        await MaterialTranslations.waitForTranslationsAsync(this.props.plant_uuid, this.props.line_group_uuid);
        this.loadViews();
        await this.checkSiloInput();
    }

    async componentDidUpdate(prev_props: Props) {
        const view_changed =
            this.props.match.params.selected_view && this.props.match.params.selected_view !== this.state.selected_view;
        const plant_changed = prev_props.plant_uuid !== this.props.plant_uuid;
        const line_group_changed = prev_props.line_group_uuid !== this.props.line_group_uuid;
        const params_changed =
            this.props.match.params.view_params &&
            prev_props.match.params.view_params !== this.props.match.params.view_params;
        if (plant_changed || line_group_changed || params_changed || view_changed) {
            await BusinessLogic.waitForManufacturingAsync();
            await MaterialTranslations.waitForTranslationsAsync(this.props.plant_uuid, this.props.line_group_uuid);
            this.loadViews();
            await this.checkSiloInput();
        }
    }

    async loadViews() {
        // Get list of lines from the plant / line_group
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        if (line_group === null) {
            return;
        }
        const line_uuids = line_group ? line_group.line_uuids : [];
        // Flag indicating we should reset url
        let reset_url = true;
        // Flag indicating user is admin
        const is_admin = Auth.isInRole(Auth.ROLE_ADMIN);
        const is_power_user = Auth.isInRole(Auth.ROLE_POWER_USER);
        const is_demo_user = Auth.isInRole(Auth.ROLE_DEMO_USER);
        const is_planner = Auth.isInRole(Auth.ROLE_MAN_PLANNER);

        // Get dashboards from the backend
        const { dashboards } = await getBackend().dashboards.searchDashboards({
            active: true,
            tags: {
                type: "man",
            },
        });

        let gantt_dashboard: string | null = null;
        let microplan_dashboard: string | null = null;
        let layout_dashboard: string | null = null;

        const line_dashboards: (DashboardViewFilterProps & { uuid: string; line_weight: number })[] = [];

        // Go over dashboards and identify relevant ones
        for (const dashboard of dashboards) {
            // Get line group gantt and microplan
            if (dashboard.tags.line_group === this.props.line_group_uuid) {
                if (dashboard.tags.subtype === "plant-gantt") {
                    gantt_dashboard = dashboard.uuid;
                } else if (dashboard.tags.subtype === "plant-microplan-main") {
                    microplan_dashboard = dashboard.uuid;
                } else if (dashboard.tags.subtype === "plant-layout") {
                    layout_dashboard = dashboard.uuid;
                }
            }
            // As a backup show plant level gantt or microplan
            if (dashboard.tags.plant === this.props.plant_uuid) {
                if (gantt_dashboard === null && dashboard.tags.subtype === "plant-gantt") {
                    gantt_dashboard = dashboard.uuid;
                } else if (microplan_dashboard === null && dashboard.tags.subtype === "plant-microplan-main") {
                    microplan_dashboard = dashboard.uuid;
                } else if (layout_dashboard === null && dashboard.tags.subtype === "plant-layout") {
                    layout_dashboard = dashboard.uuid;
                }
            }
            // Get lines dashboards
            const line_uuid = dashboard.tags.line;
            if (dashboard.tags.subtype === "line" && line_uuids.includes(line_uuid)) {
                const line_weight = BusinessLogic.getLineWeight(line_uuid);
                line_dashboards.push({
                    uuid: line_uuid,
                    dashboard: dashboard.uuid,
                    title: dashboard.title,
                    line_weight,
                });
            }
        }

        // Sort lines by their weights
        line_dashboards.sort((a, b) => a.line_weight - b.line_weight);

        // Create views
        const views: ViewsDropdownOption[] = [];
        let default_view_id: string | null = null;
        // Gantt dashboard
        if (gantt_dashboard && line_group.tags && line_group.tags[LINE_GROUP_TAGS.gannt] === "true") {
            views.push({
                id: VIEW_TYPES.gantt.id,
                type: VIEW_TYPES.gantt.id,
                title: VIEW_TYPES.gantt.title,
                title_code: VIEW_TYPES.gantt.title_code,
                dashboard: gantt_dashboard,
            });
            default_view_id = VIEW_TYPES.gantt.id;
        }
        // Microplan dashboards and planning views
        if (line_group.tags && line_group.tags[LINE_GROUP_TAGS.microplan] === "true") {
            if (microplan_dashboard || line_dashboards.length > 0) {
                // Prepare filters
                const microplan_filter: DashboardFilterProps = { views: [], selected: 0 };
                // Add dashboard for all lines
                if (microplan_dashboard) {
                    microplan_filter.views.push({
                        title: "All lines",
                        title_code: "Manufacturing.LinesView.all_lines",
                        dashboard: microplan_dashboard,
                    });
                }
                // Add dashboards for individual lines
                for (const line of line_dashboards) {
                    microplan_filter.views.push({
                        title: line.title,
                        dashboard: line.dashboard,
                        line_uuid: line.uuid,
                    });
                }
                // Check if any line selected in url
                if (
                    this.props.match.params.selected_view &&
                    this.props.match.params.selected_view === VIEW_TYPES.microplan.id &&
                    this.props.match.params.view_params
                ) {
                    const view_param = this.props.match.params.view_params;
                    for (let i = 0; i < microplan_filter.views.length; i++) {
                        if (microplan_filter.views[i].line_uuid && microplan_filter.views[i].line_uuid === view_param) {
                            microplan_filter.selected = i;
                            break;
                        }
                    }
                    reset_url = false;
                }
                // Remember the filter state
                this.setState({ microplan_filter });
                // Prepare view, with the first dashboard as leading
                views.push({
                    id: VIEW_TYPES.microplan.id,
                    type: VIEW_TYPES.microplan.id,
                    title: VIEW_TYPES.microplan.title,
                    title_code: VIEW_TYPES.microplan.title_code,
                    dashboard: microplan_filter.views[0].dashboard,
                });
            }
            // We show planing only when we have microplan enabled
            views.push({
                id: VIEW_TYPES.orders_and_shifts.id,
                type: VIEW_TYPES.orders_and_shifts.id,
                title: VIEW_TYPES.orders_and_shifts.title,
                title_code: VIEW_TYPES.orders_and_shifts.title_code,
            });
            if (
                (is_admin || is_power_user || is_demo_user || is_planner) &&
                line_group.tags[LINE_GROUP_TAGS.planning_board] === "true"
            ) {
                views.push({
                    id: VIEW_TYPES.planning_table.id,
                    type: VIEW_TYPES.planning_table.id,
                    title: VIEW_TYPES.planning_table.title,
                    title_code: VIEW_TYPES.planning_table.title_code,
                });
            }
            views.push({
                id: VIEW_TYPES.plan_change.id,
                type: VIEW_TYPES.plan_change.id,
                title: VIEW_TYPES.plan_change.title,
                title_code: VIEW_TYPES.plan_change.title_code,
            });
            views.push({
                id: VIEW_TYPES.plan_insights.id,
                type: VIEW_TYPES.plan_insights.id,
                title: VIEW_TYPES.plan_insights.title,
                title_code: VIEW_TYPES.plan_insights.title_code,
            });
        }
        // Batch operations view
        if (line_group.tags && line_group.tags[LINE_GROUP_TAGS.batch_op] === "true") {
            views.push({
                id: VIEW_TYPES.batch_op.id,
                type: VIEW_TYPES.batch_op.id,
                title: VIEW_TYPES.batch_op.title,
                title_code: VIEW_TYPES.batch_op.title_code,
            });
        }
        if (line_group.tags && line_group.tags[LINE_GROUP_TAGS.subcontracting] === "true") {
            views.push({
                id: VIEW_TYPES.subcontracting.id,
                type: VIEW_TYPES.subcontracting.id,
                title: VIEW_TYPES.subcontracting.title,
                title_code: VIEW_TYPES.subcontracting.title_code,
            });
        }
        // Manual order entry
        if (LINE_GROUP_TAGS_ACCESS.manual_order_entry(line_group.tags)) {
            views.push({
                id: VIEW_TYPES.manual_order_entry.id,
                type: VIEW_TYPES.manual_order_entry.id,
                title: VIEW_TYPES.manual_order_entry.title,
                title_code: VIEW_TYPES.manual_order_entry.title_code,
            });
        }
        // Layout dasboards
        if (layout_dashboard) {
            views.push({
                id: VIEW_TYPES.layout.id,
                type: VIEW_TYPES.layout.id,
                title: VIEW_TYPES.layout.title,
                title_code: VIEW_TYPES.layout.title_code,
                dashboard: layout_dashboard,
            });
        }
        // Realization view
        if (line_group.tags && line_group.tags[LINE_GROUP_TAGS.weekly_realization] === "true") {
            views.push({
                id: VIEW_TYPES.realization.id,
                type: VIEW_TYPES.realization.id,
                title: VIEW_TYPES.realization.title,
                title_code: VIEW_TYPES.realization.title_code,
            });
            views.push({
                id: VIEW_TYPES.downtimes.id,
                type: VIEW_TYPES.downtimes.id,
                title: VIEW_TYPES.downtimes.title,
                title_code: VIEW_TYPES.downtimes.title_code,
            });
        }

        // Comparison of prediction vs realization for admins
        if (is_admin || is_power_user || is_demo_user) {
            views.push({
                id: VIEW_TYPES.prediction_vs_real.id,
                type: VIEW_TYPES.prediction_vs_real.id,
                title: VIEW_TYPES.prediction_vs_real.title,
                title_code: VIEW_TYPES.prediction_vs_real.title_code,
            });
        }

        if (line_group.tags && LINE_GROUP_TAGS_ACCESS.preplanning(line_group.tags)) {
            views.push({
                id: VIEW_TYPES.preplanning.id,
                type: VIEW_TYPES.preplanning.id,
                title: VIEW_TYPES.preplanning.title,
                title_code: VIEW_TYPES.preplanning.title_code,
            });
        }

        // Assign appropriate view
        let selected_view = default_view_id || (views.length > 0 && views[0].id ? views[0].id : "");
        const url_view = this.props.match.params.selected_view;
        if (url_view) {
            // We have view selected by the URL, check if even possible
            if (views.find(view => view.id === url_view)) {
                // All is good, mark it selected
                selected_view = url_view;
            }
        } else {
            // No view indicated in url, use default one
        }

        this.setState({ views, selected_view });
        if (reset_url) {
            this.props.history.push({
                pathname: `/manufacturing/lines/${selected_view}`,
                search: this.props.history.location.search,
            });
        }
        // Reset filters that need reseting
        this.setState({ batch_archive: false });
        this.setState({ batch_status: false });
    }

    handleViewChange = (event: HandleViewChangeEvent) => {
        // Read new value
        const selected_view = (event.currentTarget as HTMLButtonElement)?.value ?? "";

        // Reset filter for some cases
        if (selected_view === VIEW_TYPES.microplan.id) {
            const microplan_filter = {
                views: this.state.microplan_filter.views,
                selected: 0,
            };
            this.setState({ microplan_filter });
        }

        // Find new id
        this.setState({ selected_view });
        redirectSelectedView(selected_view, this.props.history);
    };

    /////////////////////////////////////////
    // Microplan
    handleMicroplanViewChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // Read new value
        const selected = parseInt(event.currentTarget.value, 10);
        const microplan_filter = {
            views: this.state.microplan_filter.views,
            selected,
        };
        this.setState({ microplan_filter });
        if (microplan_filter.views[selected].line_uuid) {
            const selected_view = this.state.selected_view;
            const line_uuid = microplan_filter.views[selected].line_uuid;
            this.props.history.push(`/manufacturing/lines/${selected_view}/${line_uuid}`);
        }
    };

    handleMicroplanPrintClick = (event: React.MouseEvent<DropdownItemProps>) => {
        const el = document.querySelector("#microplan");

        if (el instanceof HTMLElement && event.target instanceof HTMLButtonElement) {
            const e = new CustomEvent("export-html-microplan", { detail: event.target.value });
            el.dispatchEvent(e);
        }
    };

    renderMicroplanFilter() {
        const show_shift_table = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.microplan_show_shift_table,
            true
        );
        const show_order_table = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.microplan_show_order_table,
            true
        );

        return (
            <>
                <li className="list-inline-item shift-table-toolbar flex-grow-1 mr-3" id="shift-table-toolbar" />
                <li className="list-inline-item">
                    <div className="form-group">
                        <label className="d-inline-block">
                            <FormattedMessage id="Manufacturing.LinesView.view" defaultMessage="View" />
                        </label>
                        <select
                            name="select_view"
                            className="form-control d-inline-block w-auto select_top_control"
                            value={this.state.microplan_filter.selected}
                            onChange={this.handleMicroplanViewChange}
                        >
                            {this.state.microplan_filter.views.map((view, i) =>
                                view.title_code ? (
                                    <option key={i} value={i}>
                                        {translate(view.title_code, view.title)}
                                    </option>
                                ) : (
                                    <option key={i} value={i}>
                                        {view.title}
                                    </option>
                                )
                            )}
                        </select>
                    </div>
                    <div className="btn-group">
                        <Dropdown>
                            <Dropdown.Toggle as="button" className="btn btn-outline-primary">
                                {translate("common.print", "Print")}
                                <i className="fas fa-chevron-down" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="right">
                                {show_order_table && show_shift_table && (
                                    <>
                                        <Dropdown.Item
                                            as="button"
                                            value={MICROPLAN_EXPORT_TYPE.all}
                                            onClick={this.handleMicroplanPrintClick}
                                        >
                                            {translate("common.print_all", "Print all")}
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                    </>
                                )}
                                {show_shift_table && (
                                    <Dropdown.Item
                                        as="button"
                                        value={MICROPLAN_EXPORT_TYPE.shifts}
                                        onClick={this.handleMicroplanPrintClick}
                                    >
                                        {translate("Manufacturing.LinesView.print_shifts", "Print shifts")}
                                    </Dropdown.Item>
                                )}
                                {show_order_table && (
                                    <Dropdown.Item
                                        as="button"
                                        value={MICROPLAN_EXPORT_TYPE.orders}
                                        onClick={this.handleMicroplanPrintClick}
                                    >
                                        {translate("Manufacturing.LinesView.print_work_orders", "Print work-orders")}
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </li>
            </>
        );
    }

    /////////////////////////////////////////
    // Microplan Summary
    handleMicroplanSummaryBreakdownChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // Read new breakdown value
        const breakdown_level = event.currentTarget.value;
        // Update filter in the state
        const microplan_summary_filter = {
            ...this.state.microplan_summary_filter,
            breakdown_level,
        };
        this.setState({ microplan_summary_filter });
    };

    handleProducedScrapDropdownChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // Read new breakdown value
        const filter = {
            ...this.state.microplan_summary_filter,
            produced_scrap_downtime: event.currentTarget.value,
        };

        this.setState({ microplan_summary_filter: filter });
    };

    handleMicroplanSummaryWeekClick(val: number) {
        // Handle year flip
        const dt = getDateOfISOWeek(this.state.microplan_summary_filter.week, this.state.microplan_summary_filter.year);
        dt.setDate(dt.getDate() + val * 7);
        const week = weekNumber(dt);
        let year = dt.getFullYear();
        const month = dt.getMonth();
        // Fixing problem, when week starts before first day of the year
        if (month === 11 && week === 1) {
            year = year + 1;
        }
        // Update state
        const microplan_summary_filter = {
            ...this.state.microplan_summary_filter,
            year,
            week,
        };
        this.setState({ microplan_summary_filter });
    }

    onExportWeeklyRealization = () => {
        const event = new Event("export-weekly-realization-for-print");
        const el = document.querySelector("#weekly-realization");

        if (el instanceof HTMLElement) {
            el.dispatchEvent(event);
        }
    };

    setExportToXLSXButtonLoadingState = (loading: boolean) => {
        this.exportToXLSXButtonRef.current?.setLoading(loading);
    };

    onWeekPickerChange = (week: number, year: number) => {
        const filter = {
            ...this.state.microplan_summary_filter,
            week,
            year,
        };

        this.setState({ microplan_summary_filter: filter });
    };

    renderMicroplanSummaryFilter() {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);

        const show_dropdown =
            line_group &&
            line_group.tags &&
            (line_group.tags[LINE_GROUP_TAGS.weekly_realization_downtimes_view] == "true" ||
                line_group.tags[LINE_GROUP_TAGS.weekly_realization_scrap_view] == "true");

        // Checks to see which views are enabled. If non of the extra views are enabled, then dropdown is hidden.
        return (
            <>
                <ToolbarGroup align="end" grow>
                    {show_dropdown && (
                        <ToolbarItem>
                            <label className="flex-grow-1 text-nowrap mb-0" htmlFor="produced-scrap-downtime-select">
                                <FormattedMessage id="common.view" defaultMessage="View" />
                            </label>
                            <select
                                className="form-control select_filter_control"
                                id="produced-scrap-downtime-select"
                                name="producedscrap"
                                value={this.state.microplan_summary_filter.produced_scrap_downtime}
                                onChange={this.handleProducedScrapDropdownChange}
                            >
                                <FormattedMessage id="common.produced" defaultMessage="Produced">
                                    {text => (
                                        <option key="produced" value="produced">
                                            {text}
                                        </option>
                                    )}
                                </FormattedMessage>
                                {line_group &&
                                    line_group.tags &&
                                    line_group.tags[LINE_GROUP_TAGS.weekly_realization_scrap_view] == "true" && (
                                        <FormattedMessage id="common.scrap" defaultMessage="Scrap">
                                            {text => (
                                                <option key="scrap" value="scrap">
                                                    {text}
                                                </option>
                                            )}
                                        </FormattedMessage>
                                    )}
                                {line_group &&
                                    line_group.tags &&
                                    line_group.tags[LINE_GROUP_TAGS.weekly_realization_downtimes_view] == "true" && (
                                        <FormattedMessage id="common.downtime" defaultMessage="Downtime">
                                            {text => (
                                                <option key="downtime" value="downtime">
                                                    {text}
                                                </option>
                                            )}
                                        </FormattedMessage>
                                    )}
                            </select>
                        </ToolbarItem>
                    )}
                    <ToolbarItem>
                        <label className="flex-grow-1 text-nowrap mb-0" htmlFor="breakdown-level-select">
                            <FormattedMessage
                                id="Manufacturing.MicroplanSummary.breakdown_report"
                                defaultMessage="Breakdown report"
                            />
                        </label>
                        <select
                            className="form-control select_filter_control"
                            id="breakdown-level-select"
                            name="breakdown_level"
                            value={this.state.microplan_summary_filter.breakdown_level}
                            onChange={this.handleMicroplanSummaryBreakdownChange}
                        >
                            <FormattedMessage id="common.shift" defaultMessage="Shift">
                                {text => (
                                    <option key="1_shift_bd" value="shift">
                                        {text}
                                    </option>
                                )}
                            </FormattedMessage>
                            <FormattedMessage id="common.day" defaultMessage="Day">
                                {text => (
                                    <option key="2_day_bd" value="day">
                                        {text}
                                    </option>
                                )}
                            </FormattedMessage>
                            <FormattedMessage id="common.week" defaultMessage="Week">
                                {text => (
                                    <option key="3_week_bd" value="week">
                                        {text}
                                    </option>
                                )}
                            </FormattedMessage>
                        </select>
                    </ToolbarItem>
                </ToolbarGroup>
                <ToolbarGroup separator="before">
                    <ToolbarItem>
                        <label className="flex-grow-1 text-nowrap mb-0">
                            <FormattedMessage id="common.week" defaultMessage="Week" />
                        </label>
                        {this.state.microplan_summary_filter.week && (
                            <WeekPicker
                                onWeekPickerChange={this.onWeekPickerChange}
                                filter_week={this.state.microplan_summary_filter.week}
                                filter_year={this.state.microplan_summary_filter.year}
                            />
                        )}
                    </ToolbarItem>
                </ToolbarGroup>
                <ToolbarGroup separator="before">
                    {this.state.microplan_summary_filter.breakdown_level === "shift" && (
                        <ToolbarItem>
                            <button
                                className="btn btn-primary w-100"
                                onClick={this.onExportWeeklyRealization}
                                disabled={this.state.microplan_summary_filter.produced_scrap_downtime !== "produced"}
                            >
                                <span>{translate("ShiftDetails.export_for_print", "Export for print")}</span>
                            </button>
                        </ToolbarItem>
                    )}
                    <ToolbarItem>
                        <ExportToXLSXButton
                            className="w-100"
                            ref={this.exportToXLSXButtonRef}
                            filter={this.state.microplan_summary_filter}
                            plant_uuid={this.props.plant_uuid}
                            line_group_uuid={this.props.line_group_uuid}
                        />
                    </ToolbarItem>
                </ToolbarGroup>
            </>
        );
    }

    /////////////////////////////////////////
    // Batch Operations
    handleBatchOperationsArchive(status: boolean) {
        this.setState({ batch_archive: status });
    }

    handleBatchOperationsStatus(status: boolean) {
        this.setState({ batch_status: status });
    }

    handleBatchOperationsOrderIdChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const batch_history_filter = this.state.batch_history_filter;
        batch_history_filter.order_id = value;
        this.setState({ batch_history_filter });
    }

    handleBatchOperationsMaterialChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const batch_history_filter = this.state.batch_history_filter;
        batch_history_filter.material_ident = value;
        this.setState({ batch_history_filter });
    }

    handleBatchOperationsMaterialQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        this.setState({ material_ident_query: value });
    }

    handleBatchOperationsDateChange(date: Date, name: string) {
        const batch_history_filter = this.state.batch_history_filter;
        const filtered_date = date;

        if (name === "start_time") {
            filtered_date.setUTCHours(0, 0, 0, 0);
            batch_history_filter.start_date = filtered_date;
        } else {
            filtered_date.setUTCHours(23, 59, 59, 99);
            batch_history_filter.end_date = filtered_date;
        }

        this.setState({ batch_history_filter });
    }

    async checkSiloInput() {
        const api = getBackend().batchOperations;
        const res = await api.getGroups({});
        const groups = res.items.filter(x => x.tags[BATCHOP_GROUP_TAGS.line_group] === this.props.line_group_uuid);
        const silo_input = groups.length > 0 ? BATCHOP_GROUP_TAGS_ACCESS.silo_input(groups[0].tags) : false;
        this.setState({ silo_input });
    }

    renderBatchOperationsFilter() {
        return (
            <li className="list-inline-item">
                <div className="form-group">
                    {this.state.batch_archive ? (
                        <div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    className="form-control search_bar"
                                    placeholder={translate("OrderTableProduction.material_id", "Material ID")}
                                    name="batchop_materialident_filter"
                                    value={this.state.material_ident_query}
                                    onChange={e => this.handleBatchOperationsMaterialQueryChange(e)}
                                    onBlur={e => this.handleBatchOperationsMaterialChange(e)}
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    className="form-control search_bar"
                                    placeholder={
                                        translate("Manufacturing.LinesView.search_order_id", "Search by order id") +
                                        " ..."
                                    }
                                    name="batchop_orderid_filter"
                                    value={this.state.batch_history_filter.order_id}
                                    onChange={e => this.handleBatchOperationsOrderIdChange(e)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`batch-operation-history-start-time`}>
                                    <FormattedMessage id="common.date_range" defaultMessage="Date Range" />
                                </label>
                                <DateTimePicker
                                    inputId={`batch-operation-history-start-time`}
                                    className="d-inline-block"
                                    inputClassName="form-control-sm"
                                    name="start_time"
                                    value={this.state.batch_history_filter.start_date}
                                    pickerType="date"
                                    popupPostion="fixed"
                                    timeUnits={[]}
                                    onChange={date => {
                                        this.handleBatchOperationsDateChange(date, "start_time");
                                    }}
                                />
                                &nbsp;-&nbsp;
                                <DateTimePicker
                                    inputId={`batch-operation-history-end-time`}
                                    className="d-inline-block"
                                    inputClassName="form-control-sm"
                                    name="start_time"
                                    minValue={this.state.batch_history_filter.start_date}
                                    value={this.state.batch_history_filter.end_date}
                                    pickerType="date"
                                    popupPostion="fixed"
                                    onChange={date => {
                                        this.handleBatchOperationsDateChange(date, "end_time");
                                    }}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    this.handleBatchOperationsArchive(false);
                                }}
                            >
                                <FormattedMessage id="common.back" defaultMessage="Back" />
                            </button>
                        </div>
                    ) : this.state.batch_status ? (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                this.handleBatchOperationsStatus(false);
                            }}
                        >
                            <FormattedMessage id="common.back" defaultMessage="Back" />
                        </button>
                    ) : this.state.silo_input ? (
                        <div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => this.handleBatchOperationsStatus(true)}
                            >
                                <FormattedMessage id="common.current_status" defaultMessage="Current status" />
                            </button>
                            &nbsp;
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => this.handleBatchOperationsArchive(true)}
                            >
                                <FormattedMessage id="common.history" defaultMessage="History" />
                            </button>
                        </div>
                    ) : (
                        <div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => this.handleBatchOperationsArchive(true)}
                            >
                                <FormattedMessage id="common.history" defaultMessage="History" />
                            </button>
                        </div>
                    )}
                </div>
            </li>
        );
    }

    /////////////////////////////////////////
    // Orders and shifts

    handleOrdersAndShiftsSave() {
        this.ordersAndShiftsRef.current?.save();
    }

    renderOrdersAndShiftsFilter() {
        return (
            <li className="list-inline-item">
                <div className="form-group">
                    <Authorization.button
                        permission={Auth.PERMISSION_NAMES.OrdersAndShiftsEdit}
                        type="button"
                        className="btn btn-primary"
                        onClick={() => this.handleOrdersAndShiftsSave()}
                    >
                        <FormattedMessage id="common.save" defaultMessage="Save" />
                    </Authorization.button>
                </div>
            </li>
        );
    }

    /////////////////////////////////////////
    // Planning table

    getGanttPastWeeks = (): number => {
        return BusinessLogic.getLineGroupTagInt(this.props.line_group_uuid, LINE_GROUP_TAGS.gantt_past_weeks, 1);
    };

    /////////////////////////////////////////
    // Lines View
    renderFilter(view: ViewsDropdownOption) {
        if (view) {
            if (view.type === VIEW_TYPES.microplan.id) {
                return this.renderMicroplanFilter();
            } else if (view.type === VIEW_TYPES.realization.id) {
                return this.renderMicroplanSummaryFilter();
            } else if (view.type === VIEW_TYPES.orders_and_shifts.id) {
                return this.renderOrdersAndShiftsFilter();
            } else if (view.type === VIEW_TYPES.batch_op.id) {
                return this.renderBatchOperationsFilter();
            } else {
                return <li className="list-inline-item"></li>;
            }
        }
        // Nothing valid selected
        return <li className="list-inline-item"></li>;
    }

    renderContent(view: ViewsDropdownOption) {
        if (view) {
            if (view.type === VIEW_TYPES.microplan.id) {
                const dashboard_uuid =
                    this.state.microplan_filter.views[this.state.microplan_filter.selected].dashboard;
                return (
                    <DashboardStandalone
                        history={this.props.history}
                        match={this.props.match}
                        uuid={dashboard_uuid}
                        asPublic={false}
                        show_filter_bar={false}
                        source_type={GanttChartSource.microplan}
                    />
                );
            } else if (view.type === VIEW_TYPES.realization.id) {
                return (
                    <MicroplanSummary
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        filter={this.state.microplan_summary_filter}
                        exportToXLSXButtonSetLoadingState={this.setExportToXLSXButtonLoadingState}
                        scrap_uptime_matrix={this.state.scrap_uptime_matrix}
                    />
                );
            } else if (view.type === VIEW_TYPES.orders_and_shifts.id) {
                return (
                    <OrdersAndShifts
                        key="orders_and_shifts"
                        history={this.props.history}
                        ref={this.ordersAndShiftsRef}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                    />
                );
            } else if (view.type === VIEW_TYPES.planning_table.id) {
                return (
                    <PlanningTableApp
                        key="planning_table_2"
                        history={this.props.history}
                        location={this.props.location}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                    />
                );
            } else if (view.type === VIEW_TYPES.batch_op.id) {
                if (this.state.batch_archive) {
                    return (
                        <BatchOperationsArchive
                            match={this.props.match}
                            showHeader={false}
                            line_group_uuid={this.props.line_group_uuid}
                            filter_order_id={this.state.batch_history_filter.order_id}
                            filter_start_date={this.state.batch_history_filter.start_date}
                            filter_end_date={this.state.batch_history_filter.end_date}
                            filter_material_ident={this.state.batch_history_filter.material_ident}
                        />
                    );
                } else if (this.state.batch_status) {
                    return (
                        <BatchOperationsStatus
                            match={this.props.match}
                            showHeader={false}
                            line_group_uuid={this.props.line_group_uuid}
                        />
                    );
                } else {
                    return (
                        <BatchOperations
                            readOnly={false}
                            showHeader={false}
                            line_group_uuid={this.props.line_group_uuid}
                        />
                    );
                }
            } else if (view.type === VIEW_TYPES.subcontracting.id) {
                return (
                    <Subcontracting plant_uuid={this.props.plant_uuid} line_group_uuid={this.props.line_group_uuid} />
                );
            } else if (view.type === VIEW_TYPES.manual_order_entry.id) {
                return (
                    <ManualEntryOrders
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                    />
                );
            } else if (view.type === VIEW_TYPES.plan_change.id) {
                const insight_types = [
                    INSIGHT_TYPES.plan_change_end,
                    INSIGHT_TYPES.plan_change_quantity,
                    INSIGHT_TYPES.plan_change_shifts,
                    INSIGHT_TYPES.plan_change_start,
                ];
                return (
                    <InsightsView
                        history={this.props.history}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        insight_types={insight_types}
                        from_diff={-8 * TIME_RANGES.HOUR}
                    />
                );
            } else if (view.type === VIEW_TYPES.plan_insights.id) {
                // Default insights related to plan data
                const insight_types = [INSIGHT_TYPES.freeze_order_move, INSIGHT_TYPES.operation_constraint_violated];
                if (
                    BusinessLogic.getLineGroupTagBool(this.props.line_group_uuid, LINE_GROUP_TAGS.stock_forecast, true)
                ) {
                    insight_types.push(INSIGHT_TYPES.operation_delay_downstream);
                    insight_types.push(INSIGHT_TYPES.operation_no_input_material);
                }
                if (BusinessLogic.getLineGroupTagBool(this.props.line_group_uuid, LINE_GROUP_TAGS.shift_board, true)) {
                    insight_types.push(INSIGHT_TYPES.no_work_in_shift);
                }
                return (
                    <InsightsView
                        history={this.props.history}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        insight_types={insight_types}
                        from_diff={-8 * TIME_RANGES.HOUR}
                        filter={insight => {
                            if (
                                insight.type === INSIGHT_TYPES.operation_no_input_material &&
                                insight.tags[INSIGHT_TAGS.operation_no_input_material_circle] === "internal"
                            ) {
                                return false;
                            }
                            return true;
                        }}
                    />
                );
            } else if (view.type === VIEW_TYPES.downtimes.id) {
                const insight_types = [INSIGHT_TYPES.man_downtime];
                return (
                    <InsightsView
                        history={this.props.history}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        insight_types={insight_types}
                        from_diff={-TIME_RANGES.WEEK}
                        reverse_order={true}
                    />
                );
            } else if (view.dashboard) {
                let source_type: GanttChartSourceTypes = GanttChartSource.gantt;
                if (view.type === VIEW_TYPES.microplan.id) {
                    source_type = GanttChartSource.microplan;
                }
                return (
                    <DashboardStandalone
                        history={this.props.history}
                        match={this.props.match}
                        uuid={view.dashboard}
                        asPublic={false}
                        show_filter_bar={false}
                        source_type={source_type}
                    />
                );
            } else if (view.type === VIEW_TYPES.prediction_vs_real.id) {
                return (
                    <PredictionVsReal plant_uuid={this.props.plant_uuid} line_group_uuid={this.props.line_group_uuid} />
                );
            } else if (view.type === VIEW_TYPES.preplanning.id) {
                return <Preplanning plant_uuid={this.props.plant_uuid} line_group_uuid={this.props.line_group_uuid} />;
            } else {
                return <div></div>;
            }
        }
        // Nothing valid selected
        return <div></div>;
    }

    renderEmpty() {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);

        return line_group ? (
            <strong>
                <FormattedMessage
                    id="Manufacturing.LinesView.no_views"
                    defaultMessage="Managing lines currently not supported for"
                />{" "}
                "{line_group.title}"
            </strong>
        ) : null;
    }

    showPlanningTableFilter = () => {
        this.setState({ show_planning_details_filter: !this.state.show_planning_details_filter });
    };

    onResetFilter = () => {
        this.props.reduxDispatch({ type: RESET_FILTERS, data: undefined });
    };

    getIsLoading(): boolean {
        const translations_loading = store.getState().translations_materials.is_loading;
        // Materials views
        const view = this.state.views.find(view => view.id === this.state.selected_view);
        if (!view?.id) return translations_loading;
        return MaterialTranslations.getMaterialsViews().includes(view.id) && translations_loading;
    }

    getViewsContentStyle = (view?: ViewsDropdownOption): React.CSSProperties | undefined => {
        if (!view) return {};

        if (this.state.selected_view !== VIEW_TYPES.planning_table.id) return undefined;

        if (view.type === VIEW_TYPES.planning_table.id) {
            return {
                paddingTop: "-5px",
            };
        }

        if (view.type === VIEW_TYPES.gantt.id) {
            return {
                paddingTop: "47px",
            };
        }

        return {};
    };

    shouldHideFilter = (view?: ViewsDropdownOption) => {
        if (!view) {
            return true;
        }

        const view_types = [
            VIEW_TYPES.microplan.id,
            VIEW_TYPES.realization.id,
            VIEW_TYPES.orders_and_shifts.id,
            VIEW_TYPES.batch_op.id,
        ];

        return !view_types.includes(view.type);
    };

    renderFilterContainer = () => {
        const view = this.state.views.find(view => view.id === this.state.selected_view);
        const hide_filter_bar = this.shouldHideFilter(view);
        if (hide_filter_bar) {
            return null;
        }
        const className = view && view.type === VIEW_TYPES.microplan.id ? "use-flex" : "";

        return (
            <div className={"views-filter-bar views_always_top " + className} key="B">
                <div className="row">
                    <div className="filter-bar-row col-lg-12 col-md-12">
                        <ul className="list-inline flex-grow-1">
                            <li className={classNames("list-inline-item", { "mr-0": view !== undefined })}>
                                <div>{!view && this.renderEmpty()}</div>
                            </li>
                            {view && this.renderFilter(view)}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    renderToolbar = () => {
        const view = this.state.views.find(view => view.id === this.state.selected_view);
        const hide_filter_bar = this.shouldHideFilter(view);
        if (hide_filter_bar) {
            return null;
        }

        return (
            <Toolbar className="views-filter-bar views_always_top">
                {!view ? this.renderEmpty() : this.renderFilter(view)}
            </Toolbar>
        );
    };

    getViewId = (view?: ViewsDropdownOption): string => {
        if (view) {
            if (view.id) {
                return view.id;
            }
        }
        return "";
    };

    render() {
        const view = this.state.views.find(view => view.id === this.state.selected_view);
        const hide_filter = this.shouldHideFilter(view);
        const viewClasses = `views-content ${hide_filter ? "hide-filter" : ""} ${this.getViewId(view)}`;

        return (
            <>
                <NavigationBarWithDropdown
                    key="A"
                    nav="views"
                    nav_id="lines"
                    location={this.props.location}
                    handleViewChange={this.handleViewChange}
                    line_group_uuid={this.props.line_group_uuid}
                    plant_uuid={this.props.plant_uuid}
                />
                {view !== undefined && view.type === VIEW_TYPES.realization.id
                    ? // TODO: Implement responsive toolbar for all views
                      this.renderToolbar()
                    : this.renderFilterContainer()}
                <div className={viewClasses} style={this.getViewsContentStyle(view)}>
                    {view && this.renderContent(view)}
                </div>
            </>
        );
    }
}

const mapDispatch = (dispatch: Dispatch<PlanningTableFiltersActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapDispatch>;

export default connect<{}, ReduxProps, OwnProps>(null, mapDispatch)(LinesView);
